import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Check } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const INTERESTS = [
  "Technology", "Design", "Healthcare", "Education", "Business", "Science",
  "Arts & Media", "Engineering", "Environment", "Social Impact",
  "Finance", "Writing", "Psychology", "Law", "Sports", "Food"
];

// Hobby-style interests shown when the user selects High School as their education level.
const HIGH_SCHOOL_INTERESTS = [
  "Soccer / Football", "Cricket", "Basketball", "Badminton", "Athletics",
  "Chess", "Video games / eSports", "Board games", "Puzzles / Rubik's cube",
  "Reading books", "Writing stories", "Drawing / sketching", "Painting", "Photography",
  "Making YouTube videos", "Editing videos", "Music (listening)", "Playing an instrument",
  "Singing", "Dancing", "Theatre / drama", "Debate / MUN", "Public speaking",
  "Coding", "Robotics", "Building things (DIY)", "Electronics / tinkering",
  "Space / astronomy", "Biology / animals", "Chemistry experiments", "Physics", "Maths olympiads",
  "Cooking / baking", "Fashion", "Fitness / gym", "Yoga", "Hiking / trekking",
  "Volunteering", "Environment / sustainability", "Pets / animal care",
  "Social media / content creation", "Trading / stock market", "Startups / business ideas",
  "History", "Geography / maps", "Learning languages", "Anime / manga", "Movies / cinema"
];

const SKILLS = [
  "Problem solving", "Communication", "Creativity", "Analytical thinking",
  "Leadership", "Empathy", "Coding", "Public speaking",
  "Teamwork", "Research", "Negotiation", "Visual design",
  "Numerical reasoning", "Storytelling", "Organization", "Strategic planning"
];

// Age-appropriate self-descriptions for high school students.
const HIGH_SCHOOL_SKILLS = [
  "Curious about how things work", "Fast learner", "Good with numbers",
  "Good at explaining things to friends", "Creative / imaginative",
  "Detail-oriented", "Persistent when stuck", "Good at teamwork",
  "Class leader / prefect type", "Comfortable on stage",
  "Good with hands / building", "Empathetic / good listener",
  "Organized planner", "Good at drawing / visual design",
  "Fast reader", "Storyteller", "Good at convincing people",
  "Athletic / physically active"
];

const WORK_STYLES = [
  { id: "independent", label: "Independent", desc: "Deep focus, my own schedule" },
  { id: "collaborative", label: "Collaborative", desc: "Teams, brainstorms, energy" },
  { id: "structured", label: "Structured", desc: "Clear rules, defined outcomes" },
  { id: "exploratory", label: "Exploratory", desc: "Open-ended, new territory" },
];

const EDU_LEVELS = [
  { id: "high_school", label: "High school" },
  { id: "some_college", label: "Some college" },
  { id: "bachelors", label: "Bachelor's degree" },
  { id: "masters", label: "Master's degree" },
  { id: "phd", label: "PhD / Doctorate" },
  { id: "self_taught", label: "Self-taught" },
];

const GOALS = [
  { id: "first_career", label: "Start my first career" },
  { id: "switch", label: "Switch into something new" },
  { id: "level_up", label: "Level up in my current field" },
  { id: "freelance", label: "Go freelance / independent" },
  { id: "purpose", label: "Find more purposeful work" },
];

export default function Quiz() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({
    interests: [],
    skills: [],
    work_style: "",
    education_level: "",
    goal: "",
    free_text: "",
  });

  const TOTAL = 6;

  const toggleArr = (key, value) => {
    setAnswers((a) => {
      const set = new Set(a[key]);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return { ...a, [key]: Array.from(set) };
    });
  };

  const isHighSchool = answers.education_level === "high_school";
  const interestOptions = isHighSchool ? HIGH_SCHOOL_INTERESTS : INTERESTS;
  const skillOptions = isHighSchool ? HIGH_SCHOOL_SKILLS : SKILLS;

  const canProceed = () => {
    if (step === 0) return !!answers.education_level;
    if (step === 1) return answers.interests.length >= 2;
    if (step === 2) return answers.skills.length >= 2;
    if (step === 3) return !!answers.work_style;
    if (step === 4) return !!answers.goal;
    return true;
  };

  const next = () => {
    if (step < TOTAL - 1) setStep(step + 1);
    else submit();
  };
  const back = () => step > 0 ? setStep(step - 1) : navigate("/");

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/quiz/submit", answers);
      navigate(`/results/${res.data.quiz_id}`);
    } catch (e) {
      console.error(e);
      toast.error("Could not generate results. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#F4F4F0]" data-testid="quiz-page">
      {/* Progress bar */}
      <div className="sticky top-[65px] z-40 bg-[#F4F4F0]/95 backdrop-blur-sm border-b border-[#E5E5E0]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-4 flex items-center gap-4">
          <span className="overline" data-testid="quiz-progress-label">
            Step {step + 1} / {TOTAL}
          </span>
          <div className="flex-1 h-1 bg-[#E5E5E0] overflow-hidden">
            <div
              className="h-full bg-[#002FA7] transition-all duration-500"
              style={{ width: `${((step + 1) / TOTAL) * 100}%` }}
              data-testid="quiz-progress-bar"
            />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-20">
        {step === 0 && (
          <StepWrap title="What's your current education level?" sub="This shapes the rest of the quiz and the recommendations you'll see.">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {EDU_LEVELS.map((e) => (
                <SelectCard
                  key={e.id}
                  active={answers.education_level === e.id}
                  onClick={() => setAnswers((a) => ({ ...a, education_level: e.id, interests: [], skills: [] }))}
                  testid={`edu-${e.id}`}
                >
                  <div className="font-heading text-xl font-bold">{e.label}</div>
                </SelectCard>
              ))}
            </div>
          </StepWrap>
        )}
        {step === 1 && (
          <StepWrap
            title={isHighSchool ? "What do you love doing?" : "What pulls your attention?"}
            sub={
              isHighSchool
                ? "Hobbies, sports, subjects, anything you enjoy in your free time. Pick as many as feel right — at least 2."
                : "Pick the topics you'd happily read about for hours. Choose at least 2."
            }
          >
            <ChipGroup
              options={interestOptions}
              selected={answers.interests}
              onToggle={(v) => toggleArr("interests", v)}
              testidPrefix="interest"
            />
          </StepWrap>
        )}
        {step === 2 && (
          <StepWrap
            title={isHighSchool ? "Which of these describes you?" : "Which skills feel natural to you?"}
            sub={
              isHighSchool
                ? "Pick the ones your friends or teachers would say about you. At least 2."
                : "Things you can do without forcing it. Choose at least 2."
            }
          >
            <ChipGroup
              options={skillOptions}
              selected={answers.skills}
              onToggle={(v) => toggleArr("skills", v)}
              testidPrefix="skill"
            />
          </StepWrap>
        )}
        {step === 3 && (
          <StepWrap title="How do you do your best work?" sub="One that fits the most.">
            <div className="grid sm:grid-cols-2 gap-4">
              {WORK_STYLES.map((w) => (
                <SelectCard
                  key={w.id}
                  active={answers.work_style === w.id}
                  onClick={() => setAnswers((a) => ({ ...a, work_style: w.id }))}
                  testid={`workstyle-${w.id}`}
                >
                  <div className="font-heading text-2xl font-bold">{w.label}</div>
                  <p className="text-sm text-[#5C5C55] mt-1">{w.desc}</p>
                </SelectCard>
              ))}
            </div>
          </StepWrap>
        )}
        {step === 4 && (
          <StepWrap title="What's the goal?" sub="The why behind this quiz.">
            <div className="grid sm:grid-cols-2 gap-4">
              {GOALS.map((g) => (
                <SelectCard
                  key={g.id}
                  active={answers.goal === g.id}
                  onClick={() => setAnswers((a) => ({ ...a, goal: g.id }))}
                  testid={`goal-${g.id}`}
                >
                  <div className="font-heading text-xl font-bold">{g.label}</div>
                </SelectCard>
              ))}
            </div>
          </StepWrap>
        )}
        {step === 5 && (
          <StepWrap title="Anything else we should know?" sub="Optional. A sentence or two about what you'd love (or want to avoid).">
            <textarea
              data-testid="freetext-input"
              value={answers.free_text}
              onChange={(e) => setAnswers((a) => ({ ...a, free_text: e.target.value }))}
              placeholder="I love combining art with technology, prefer not to travel much, want to work on climate problems…"
              rows={6}
              className="w-full bg-white border border-[#E5E5E0] rounded-sm p-5 text-base focus:border-[#002FA7] outline-none transition-colors resize-none"
            />
          </StepWrap>
        )}

        {/* Footer nav */}
        <div className="mt-12 flex items-center justify-between">
          <button
            onClick={back}
            disabled={submitting}
            data-testid="quiz-back-button"
            className="btn-secondary disabled:opacity-50"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button
            onClick={next}
            disabled={!canProceed() || submitting}
            data-testid="quiz-next-button"
            className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
            ) : step === TOTAL - 1 ? (
              <>See my matches <ArrowRight className="w-4 h-4" /></>
            ) : (
              <>Next <ArrowRight className="w-4 h-4" /></>
            )}
          </button>
        </div>
      </div>
    </main>
  );
}

function StepWrap({ title, sub, children }) {
  return (
    <div className="fade-up">
      <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95] text-[#0A0A0A] max-w-3xl">
        {title}
      </h1>
      {sub && <p className="mt-4 text-base text-[#5C5C55] max-w-2xl leading-relaxed">{sub}</p>}
      <div className="mt-10">{children}</div>
    </div>
  );
}

function ChipGroup({ options, selected, onToggle, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-3">
      {options.map((opt) => {
        const active = selected.includes(opt);
        return (
          <button
            key={opt}
            data-testid={`${testidPrefix}-${opt.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => onToggle(opt)}
            className={`px-5 py-3 border-2 rounded-sm text-sm font-semibold transition-all flex items-center gap-2 ${
              active
                ? "border-[#002FA7] bg-[#002FA7] text-white"
                : "border-[#E5E5E0] bg-white text-[#0A0A0A] hover:border-[#0A0A0A]"
            }`}
          >
            {active && <Check className="w-4 h-4" />}
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function SelectCard({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left p-6 sm:p-7 border-2 rounded-sm transition-all hover:-translate-y-1 ${
        active
          ? "border-[#002FA7] bg-[#002FA7]/5 ring-2 ring-[#002FA7]"
          : "border-[#E5E5E0] bg-white hover:border-[#0A0A0A]"
      }`}
    >
      {children}
    </button>
  );
}
